// Persist mini-app output files across full page reloads (tab discard).
//
// Uses IndexedDB (large-blob friendly, survives page reload).
// Keyed by projectId so each mini-app has its own cache slot.
// Auto-expires after MAX_AGE_MS to avoid stale data hoarding.

const DB_NAME = 'llambada-output-cache';
const STORE_NAME = 'files';
const DB_VERSION = 1;
const MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

interface CachedRun {
  projectId: string;
  files: Array<{ path: string; data: ArrayBuffer }>;
  inputFiles?: Array<{ path: string; data: ArrayBuffer; contentType: string }>;
  outputFilesMeta: Array<{ filename: string; size: number }>;
  isComplete: boolean;
  error: string | null;
  timestamp: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'projectId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Save (or update) the cached run for a project. */
export async function saveCachedRun(run: CachedRun): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(run);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.warn('[outputFileCache] Failed to save:', err);
  }
}

/** Load the cached run for a project (returns null if missing or expired). */
export async function loadCachedRun(projectId: string): Promise<CachedRun | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(projectId);
    const result = await new Promise<CachedRun | undefined>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result as CachedRun | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();

    if (!result) return null;
    // Expire stale entries
    if (Date.now() - result.timestamp > MAX_AGE_MS) {
      clearCachedRun(projectId).catch(() => {});
      return null;
    }
    return result;
  } catch (err) {
    console.warn('[outputFileCache] Failed to load:', err);
    return null;
  }
}

/** Remove the cached run for a project. */
export async function clearCachedRun(projectId: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(projectId);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.warn('[outputFileCache] Failed to clear:', err);
  }
}
